import { Club } from '@repo/types';
import { ClubNameType } from '../../../fixtures/contracts';

type NamedClub = Pick<Club, 'fullName' | 'shortName' | 'threeLetterName' | 'nickName'>;

/**
 * The name a `ClubCard` shows for a club, given the `nameType` its call site chose.
 *
 * Extracted from the card so a caller that has to *order* a list of cards can sort on the same
 * string the cards will display (SC-304). Sorting by `fullName` while the cards render nicknames
 * gives a list that is alphabetical only by a string nobody sees — and in logo-only mode the
 * names exist solely in the accessibility tree, so a screen-reader user gets the jumble with no
 * visual context to explain it.
 *
 * The `FullName` default matches `ClubCard`'s own default parameter, which is what keeps a
 * caller that passes no `nameType` sorting by the same name the card then renders.
 */
export const resolveClubName = (club: NamedClub, nameType: ClubNameType = ClubNameType.FullName): string => {
  switch (nameType) {
    case ClubNameType.ThreeLetterName:
      return club.threeLetterName ?? 'N/A';
    case ClubNameType.FullName:
      return club.fullName ?? 'N/A';
    case ClubNameType.NickName:
      return club.nickName ?? 'N/A';
    case ClubNameType.ShortName:
      return club.shortName ?? 'N/A';
    default:
      return club.shortName ?? 'N/A';
  }
};
